"use client";

type ChartWidgetModule = typeof import("@/app/workspace/ChartWidget");
type ChartCanvasModule = typeof import("@/app/workspace/ChartBlockCanvas");

type ChartCodeImports<WidgetModule, CanvasModule> = {
  importWidget: () => Promise<WidgetModule>;
  importCanvas: () => Promise<CanvasModule>;
};

/** Изолированное состояние двух независимых импортов. Фабрика позволяет
 * проверять дедупликацию и повтор после ошибки без глобального module cache. */
export function createChartCodeLoader<WidgetModule, CanvasModule>({
  importWidget,
  importCanvas,
}: ChartCodeImports<WidgetModule, CanvasModule>) {
  let widgetPromise: Promise<WidgetModule> | null = null;
  let loadedWidgetModule: WidgetModule | null = null;
  let canvasPromise: Promise<CanvasModule> | null = null;

  function getLoadedWidgetModule(): WidgetModule | null {
    return loadedWidgetModule;
  }

  function loadCanvasModule(): Promise<CanvasModule> {
    if (!canvasPromise) {
      canvasPromise = importCanvas().catch(error => {
        canvasPromise = null;
        throw error;
      });
    }
    return canvasPromise;
  }

  function loadWidgetModule(): Promise<WidgetModule> {
    if (!widgetPromise) {
      widgetPromise = importWidget().then(module => {
        loadedWidgetModule = module;
        return module;
      }).catch(error => {
        widgetPromise = null;
        throw error;
      });
    }
    return widgetPromise;
  }

  function preloadWidgetCode(): void {
    void loadWidgetModule().catch(() => {});
  }

  return { getLoadedWidgetModule, loadCanvasModule, loadWidgetModule, preloadWidgetCode };
}

const chartCodeLoader = createChartCodeLoader<ChartWidgetModule, ChartCanvasModule>({
  importWidget: () => import("@/app/workspace/ChartWidget"),
  importCanvas: () => import("@/app/workspace/ChartBlockCanvas"),
});

/** Уже подготовленный код можно отрисовать без нового цикла Suspense.
 * Это ссылка на тот же модуль, не второй импорт и не копия данных. */
export function getLoadedChartWidgetModule(): ChartWidgetModule | null {
  return chartCodeLoader.getLoadedWidgetModule();
}

/** Общий импорт для подготовки страницы и next/dynamic. Никаких данных
 * магазина: импорт не монтирует график и не запускает его API-запросы. */
export function loadChartCanvasModule(): Promise<ChartCanvasModule> {
  return chartCodeLoader.loadCanvasModule();
}

/** Ранний прогрев и React.lazy используют один импорт виджета. Тяжёлый
 * холст здесь не запускаем: его выполнение конкурировало бы с открытием
 * редактора. Виджет подготовит холст одновременно со своим запросом данных. */
export function loadChartWidgetModule(): Promise<ChartWidgetModule> {
  return chartCodeLoader.loadWidgetModule();
}

/** Вызывается только для документов/планов с chartBlock. Promise и модули
 * общие для всех диаграмм вкладки; фоновой ошибки нет в пути навигации. */
export function preloadChartCode(): void {
  chartCodeLoader.preloadWidgetCode();
}
